import { useMemo } from "react";

type WindowedListOptions = {
  itemHeight: number;
  viewportHeight: number;
  overscan?: number;
  scrollTop: number;
};

export function useWindowedList<T>(items: T[], options: WindowedListOptions) {
  const { itemHeight, viewportHeight, scrollTop, overscan = 6 } = options;

  return useMemo(() => {
    if (itemHeight <= 0 || viewportHeight <= 0) {
      return {
        totalHeight: 0,
        startIndex: 0,
        endIndex: items.length - 1,
        topSpacerHeight: 0,
        bottomSpacerHeight: 0,
        visibleItems: items,
      };
    }

    const rawStart = Math.floor(scrollTop / itemHeight);
    const visibleCount = Math.ceil(viewportHeight / itemHeight);
    const startIndex = Math.max(0, rawStart - overscan);
    const endIndex = Math.min(items.length - 1, rawStart + visibleCount + overscan);
    const topSpacerHeight = startIndex * itemHeight;
    const renderedCount = endIndex >= startIndex ? endIndex - startIndex + 1 : 0;
    const bottomSpacerHeight = Math.max(
      0,
      items.length * itemHeight - topSpacerHeight - renderedCount * itemHeight,
    );

    return {
      totalHeight: items.length * itemHeight,
      startIndex,
      endIndex,
      topSpacerHeight,
      bottomSpacerHeight,
      visibleItems: items.slice(startIndex, endIndex + 1),
    };
  }, [itemHeight, items, overscan, scrollTop, viewportHeight]);
}

